import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { IcontestDataResponse, IcontestUser } from 'app/features/contest/models/contest-api.models';
import {
  ContestApi,
  ContestDataMode,
  LEADERBOARD_MODE,
} from 'app/features/contest/data-access/api/contest-api.types';
import { ContestMapperService } from 'app/features/contest/data-access/contest-mapper.service';
import { ContestStore } from '../contest-store';
import { firstValueFrom } from 'rxjs';
import { LeaderboardUserData } from 'app/features/contest/models/leaderboard-user-data';
import { PaginationMeta } from 'app/shared/models/pagination.models';
import { environment } from 'src/environments/environment';

type FetchParticipantsOptions = {
  highlightNewRows?: boolean;
  showSkeleton?: boolean;
};

@Injectable()
export class LeaderboardService {
  private readonly api: ContestApi = inject(ContestApi);
  private readonly mapper: ContestMapperService = inject(ContestMapperService);
  private readonly contestStore: ContestStore = inject(ContestStore);
  private mode: ContestDataMode = inject(LEADERBOARD_MODE);

  private page: number = 0;
  private hasLoadedCurrentPage: boolean = false;
  private latestRequestId: number = 0;
  private rawParticipants: IcontestUser[] = [];

  error: WritableSignal<null | unknown> = signal<null | unknown>(null);
  isLoading: WritableSignal<boolean> = signal<boolean>(false);
  participants: WritableSignal<LeaderboardUserData[]> = signal<LeaderboardUserData[]>([]);
  pagination: WritableSignal<PaginationMeta | null> = signal<PaginationMeta | null>(null);

  changePage(newPage: number): Promise<void> {
    if (newPage < 0 || newPage === this.page) return Promise.resolve();

    this.page = newPage;
    this.hasLoadedCurrentPage = false;
    return this.fetchParticipants({ showSkeleton: true });
  }

  changeMode(mode: ContestDataMode): void {
    if (this.mode === mode) return;

    this.mode = mode;
    this.reset();
  }

  refresh(): Promise<void> {
    return this.fetchParticipants({
      highlightNewRows: this.hasLoadedCurrentPage,
      showSkeleton: !this.hasLoadedCurrentPage,
    });
  }

  reset(): void {
    this.page = 0;
    this.hasLoadedCurrentPage = false;
    void this.fetchParticipants({ showSkeleton: true });
  }

  async fetchParticipants(options: FetchParticipantsOptions = {}): Promise<void> {
    const requestId: number = ++this.latestRequestId;
    const showSkeleton: boolean = options.showSkeleton === true;

    this.error.set(null);
    this.isLoading.set(showSkeleton);

    const previousRowKeys: Set<string> = new Set<string>(
      this.rawParticipants.map((participant: IcontestUser) => this.getRowKey(participant))
    );
    try {
      const contestResponse: IcontestDataResponse = await firstValueFrom(
        this.api.getContestData({
          page: this.page,
          mode: this.mode,
          pprFilter: this.contestStore.pprFilter(),
          targetGender: this.contestStore.targetGender(),
          username: this.contestStore.username(),
          hashid: this.contestStore.hashid(),
        })
      );

      if (requestId !== this.latestRequestId) return;

      const lastPageIndex: number = Math.max(0, contestResponse.meta.last_page - 1);

      if (this.page > lastPageIndex) {
        this.page = lastPageIndex;
        this.hasLoadedCurrentPage = false;
        await this.fetchParticipants({ showSkeleton: true });
        return;
      }

      contestResponse.data.map(
        (user: IcontestUser) =>
          (user.ranking = user.ranking + environment.contestInfo.userPerPage * this.page)
      );

      this.error.set(null);
      this.rawParticipants = contestResponse.data;
      this.pagination.set(contestResponse.meta);

      const nextParticipants: LeaderboardUserData[] = this.mapper.toLeaderboardUsers(
        this.rawParticipants
      );

      const shouldHighlightNewRows: boolean =
        options.highlightNewRows === true && this.page === 0 && this.hasLoadedCurrentPage;

      this.participants.set(
        shouldHighlightNewRows
          ? nextParticipants.map((participant: LeaderboardUserData, index: number) => ({
              ...participant,
              highlight: !previousRowKeys.has(this.getRowKey(this.rawParticipants[index])),
            }))
          : nextParticipants
      );
      this.hasLoadedCurrentPage = true;
    } catch (error: unknown) {
      if (requestId !== this.latestRequestId) return;

      this.error.set(error);
    } finally {
      if (requestId === this.latestRequestId) this.isLoading.set(false);
    }
  }

  private getRowKey(participant: IcontestUser): string {
    if (participant.activity_id !== null && participant.activity_id !== undefined)
      return `activity:${participant.activity_id}`;

    if (participant.email) return `email:${participant.email}`;

    const fullName: string =
      `${participant.first_name ?? ''} ${participant.last_name ?? ''}`.trim() || 'anonymous';

    return `fallback:${fullName}-${participant.start_date}`;
  }
}
